"use client"
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { House } from './houses'
import { Map as OriginalMap, MapProps, useMap } from '@vis.gl/react-google-maps'
import MarkerWithInfoWindow from './marker-with-info-window'
import { calculateSphericalCenter, calculateZoomLevel } from '../../../../shared/lib/utils'
import { AdvancedMarker } from '@vis.gl/react-google-maps'

interface Props extends MapProps {
  selectedHouse?: House | null;
  houses: House[];
  fallbackCenter?: { lat: number; lng: number };
  fallbackZoom?: number;
  baseMarkerPosition?: { lat: number; lng: number } | null;
}

function Map({ selectedHouse, houses, fallbackCenter, fallbackZoom = 12, baseMarkerPosition, ...mapsProps }: Props) {
  const [selected, setSelected] = useState<House | null>(null)
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null)
  const map = useMap();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const mapCenter = useMemo(() => {
    if (houses.length > 0) {
      // Use simple geographic center instead of spherical center to avoid longitude wrapping issues
      const positions = houses.map(h => h.position);
      const latSum = positions.reduce((sum, pos) => sum + pos.lat, 0);
      const lngSum = positions.reduce((sum, pos) => sum + pos.lng, 0);
      const center = {
        lat: latSum / positions.length,
        lng: lngSum / positions.length
      };
      return center;
    }
    if (fallbackCenter) {
      return fallbackCenter;
    }
    return { lat: 35.6106, lng: -82.3965 }; // Default fallback
  }, [houses, fallbackCenter]);
  const zoomLevel = useMemo(() => {
    if (houses.length === 0) return fallbackZoom;
    if (houses.length === 1) return 15; // Single house - zoom in closer
    
    const positions = houses.map(h => h.position);
    
    // Calculate the bounding box of all houses
    const lats = positions.map(p => p.lat);
    const lngs = positions.map(p => p.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    
    // Calculate the span of coordinates
    const latSpan = maxLat - minLat;
    const lngSpan = maxLng - minLng;
    const maxSpan = Math.max(latSpan, lngSpan);
    
    // Improved zoom calculation that fits homes better on the map
    // Much more aggressive zoom levels to ensure homes fill most of the map
    let zoom = 15; // Default to a closer zoom level
    
    // Calculate zoom based on coordinate span - much more aggressive
    if (maxSpan <= 0.0001) zoom = 18;      // Very close (< 10m)
    else if (maxSpan <= 0.0005) zoom = 17; // Close (< 50m) 
    else if (maxSpan <= 0.001) zoom = 16;  // Very close (< 100m)
    else if (maxSpan <= 0.002) zoom = 15;  // Close (< 200m)
    else if (maxSpan <= 0.005) zoom = 14;  // Neighborhood (< 500m)
    else if (maxSpan <= 0.01) zoom = 13;   // Local area (< 1km)
    else if (maxSpan <= 0.02) zoom = 12;   // Small city area (< 2km)
    else if (maxSpan <= 0.05) zoom = 11;   // Medium city area (< 5km)
    else if (maxSpan <= 0.1) zoom = 10;    // Large city area (< 10km)
    else if (maxSpan <= 0.2) zoom = 9;     // Regional (< 20km)
    else if (maxSpan <= 0.5) zoom = 8;     // Large regional (< 50km)
    else if (maxSpan <= 1.0) zoom = 7;     // Multi-city (< 100km)
    else zoom = 6; // Very large area fallback
    
    return zoom;
  }, [houses, fallbackZoom]);

  // Effect to fit bounds when houses change or map loads
  useEffect(() => {
    if (map && (houses.length > 0 || baseMarkerPosition)) {
      // Use Google Maps fitBounds for more accurate zoom fitting
      const bounds = new google.maps.LatLngBounds();
      houses.forEach(house => {
        bounds.extend(new google.maps.LatLng(house.position.lat, house.position.lng));
      });
      if (baseMarkerPosition) {
        bounds.extend(new google.maps.LatLng(baseMarkerPosition.lat, baseMarkerPosition.lng));
      }
      
      // Fit the map to show all houses with padding
      map.fitBounds(bounds, {
        top: 50,
        right: 50,
        bottom: 50,
        left: 50
      });
      
    }
  }, [map, houses, baseMarkerPosition]);

  useEffect(() => {
    if (selectedHouse && map) {
      map.setCenter(selectedHouse.position);
      map.setZoom(18);
      setActiveMarkerId(selectedHouse.id);
      setSelected(selectedHouse);
    }
  }, [selectedHouse, map]);

  const handleMarkerClick = useCallback((id: string) => {
    setActiveMarkerId(prev => prev === id ? null : id);
    setSelected(houses.find(house => house.id === id) || null);
  }, [houses]);

  const handleInfoWindowClose = useCallback(() => {
    setActiveMarkerId(null);
  }, [])

  
  return (
    <OriginalMap
      key={`map-${houses.length}-${zoomLevel}-${JSON.stringify(mapCenter)}`}
      className='h-[500px] md:h-[500px] lg:h-[700px] rounded overflow-hidden border-none'
      defaultCenter={mapCenter}
      defaultZoom={zoomLevel}
      mapTypeId="roadmap"
      disableDefaultUI={true}
      mapTypeControl
      mapId="LOCATION_MAP_ID"
      {...mapsProps}
    >
      {baseMarkerPosition && (
        <AdvancedMarker position={baseMarkerPosition}>
          <div className="relative flex items-center justify-center bg-white w-9 h-9 rounded-full shadow-md">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-semibold">
              HQ
            </div>
          </div>
        </AdvancedMarker>
      )}

      {houses.map((house) => (
        <MarkerWithInfoWindow
          key={house.id}
          house={house}
          activeMarkerId={activeMarkerId}
          onMarkerClick={handleMarkerClick}
          onInfoWindowClose={handleInfoWindowClose}
        />
      ))}
    </OriginalMap>
  )
}

export default React.memo(Map)
